import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { isEqual, isEmpty, isNumber, isBoolean, cloneDeep } from 'lodash'
import filterChildrenService from '@/services/http/api/filterChildren'
import resource from '@/services/http/api/resource'
import { usePaginationStore } from './pagination'
import type { TODO } from '@/types'

// @TODO: add types based on the API
const defaultParameters = () => ({
  provinces: [],
  wioStructLocationIds: [],
  volunteer: null,
  mainGiftGiver: null,
  seniority: null,
  documentType: null,
  documentStatus: [],
  childEditionStatus: [],
  sortBy: null,
  sortOrder: null,
  search: null
})

const prepareData = (payload: TODO) => {
  const data = cloneDeep(payload)
  if (typeof data === 'object') {
    Object.keys(data).forEach((key) => {
      const dataValue = data[key]
      data[key] = isNumber(dataValue) ? String(dataValue) : dataValue
    })
  }

  const payloadFormat = {
    offset: data?.offset || null,
    provinces: (isEmpty(data?.wioStructLocationIds) && data?.provinces) || null,
    wioStructLocationIds: data?.wioStructLocationIds || null,
    volunteer: data?.volunteer || null,
    mainGiftGiver: data?.mainGiftGiver || null,
    seniority: data?.seniority || null,
    documentType: data?.documentType || null,
    documentStatus: (!isEmpty(data?.documentType) && data?.documentStatus) || null,
    childEditionStatus: data?.childEditionStatus || null,
    sortBy: data?.sortBy || null,
    sortOrder: data?.sortOrder || null,
    search: data?.search || null
  }

  const entries = Object.entries(payloadFormat).filter(([, v]) => isBoolean(v) || !isEmpty(v))
  return Object.fromEntries(entries)
}

export const useChildrenFilterStore = defineStore('childrenFilter', () => {
  const loader = ref<boolean>(false)
  const childrenNumber = ref<number | null>(null)
  const childrenList = ref<TODO>(null)
  const resourceFilter = ref<Record<string, unknown> | null>(null)
  const schoolsWithProvinceResource = ref<Record<string, unknown> | null>(null)
  const parameters = ref<TODO>(defaultParameters())

  const paginationStore = usePaginationStore()

  const areDefaultParameters = computed(() => isEqual(parameters.value, defaultParameters()))

  function setLoader(value: boolean) {
    loader.value = value
  }

  function setChildrenList(list: unknown[]) {
    childrenList.value = list
  }

  function setChildrenNumber(number: number) {
    childrenNumber.value = number
  }

  function updateChildrenListItem(payload: TODO = []) {
    if (!payload || !childrenList.value) return null
    const index = childrenList.value.findIndex((item: TODO) => item?.id === payload?.id)
    if (index !== -1) {
      childrenList.value[index] = { ...payload }
    }
  }

  function setParameters(payload: TODO) {
    parameters.value = !payload || payload?.length === 0 ? defaultParameters() : payload
  }

  function clearParameters() {
    parameters.value = defaultParameters()
  }

  async function getChildren() {
    const offset = paginationStore.getOffset()
    const urlSearchParams = new URLSearchParams()
    urlSearchParams.append('offset', offset.toString())

    setLoader(true)
    try {
      const { data } = await filterChildrenService.getChildren(urlSearchParams)
      setChildrenList(data.data)
      setChildrenNumber(data.number)
      return data.data
    } finally {
      if (urlSearchParams.toString() === '') {
        paginationStore.clearPagination()
      }
      setLoader(false)
    }
  }

  async function filterChildren() {
    if (!filterChildrenService?.filterChildren) return null

    const offset = paginationStore.getOffset()
    const payloadData = prepareData({ offset, ...parameters.value })

    setLoader(true)
    try {
      const { data } = await filterChildrenService.filterChildren(payloadData)
      setChildrenList(data?.data || null)
      setChildrenNumber(data?.number || null)
      return data.data
    } finally {
      setLoader(false)
    }
  }

  async function getCoordinatorsChildren() {
    if (!filterChildrenService?.getCoordinatorsChildren) return null

    const offset = paginationStore.getOffset()
    const urlSearchParams = new URLSearchParams()
    urlSearchParams.append('offset', offset.toString())

    setLoader(true)
    try {
      const { data } = await filterChildrenService.getCoordinatorsChildren(urlSearchParams)
      setChildrenList(data?.data)
      setChildrenNumber(data?.number)
      return data?.data
    } finally {
      if (urlSearchParams.toString() === '') {
        paginationStore.clearPagination()
      }
      setLoader(false)
    }
  }

  async function getResourceFilter() {
    if (!resource?.childrenListFilterData) return null

    const { data } = await resource.childrenListFilterData()
    if (data?.data) {
      resourceFilter.value = data.data
      return data.data
    }
    return null
  }

  async function getSchoolsWithProvinceResource() {
    if (!resource?.schoolsWithProvinceFilterData) return null

    const { data } = await resource.schoolsWithProvinceFilterData()
    if (data?.data) {
      schoolsWithProvinceResource.value = data.data
      return data.data
    }
    return null
  }

  return {
    // State
    loader,
    childrenNumber,
    childrenList,
    resourceFilter,
    schoolsWithProvinceResource,
    parameters,
    // Getters
    areDefaultParameters,
    // Actions
    setLoader,
    setChildrenList,
    setChildrenNumber,
    updateChildrenListItem,
    setParameters,
    clearParameters,
    getChildren,
    filterChildren,
    getCoordinatorsChildren,
    getResourceFilter,
    getSchoolsWithProvinceResource
  }
})
